<template>
  <admin-layout :title="$t('Dashboard')">
    <form ref="exportList" action="/report/export" method="post">
      <input type="hidden" name="_token" :value="token" />
      <div class="px-4 md:px-0">
        <tec-section-title class="-mx-4 md:mx-0 mb-6">
          <template #title>{{ $t('Dashboard') }}</template>
          <template #description>{{ $t('Please review the data in the chart below') }}</template>
        </tec-section-title>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
          <div class="w-full">
            <text-input type="date" name="start_date" format="yyyy-mm-dd" v-model="form.start_date" :label="$t('Start Date')" />
          </div>

          <div class="w-full">
            <text-input type="date" name="end_date" format="yyyy-mm-dd" v-model="form.end_date" :label="$t('End Date')" />
          </div>

          <div class="w-full">
            <v-select-input :label="$t('Customer')">
              <input type="hidden" name="customer_id" :value="form.customer_id" />
              <v-select v-model="form.customer_id" :options="customers" :reduce="customer => customer.id" label="name">
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </template>
              </v-select>
            </v-select-input>
          </div>

          <div class="w-full">
            <v-select-input :label="$t('Agent')">
              <input type="hidden" name="agent_id" :value="form.agent_id" />
              <v-select v-model="form.agent_id" :options="users" :reduce="user => user.id" label="name" name="agent_id">
                <template #open-indicator="{ attributes }">
                  <span v-bind="attributes">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </span>
                </template>
              </v-select>
            </v-select-input>
          </div>

          <!-- <auto-complete v-model="month" :suggestions="months" class="w-1/2" @update:modelValue="reload" />
        <auto-complete v-model="year" :suggestions="years" class="w-1/2" @update:modelValue="reload" /> -->
        </div>
        <div class="flex justify-end">
          <loading-button :loading="form.processing" :disabled="form.processing" @click="reload">{{ $t('Get Report') }}</loading-button>
        </div>

        <section v-if="data" class="my-4 mx-auto">
          <h2 class="mb-2 font-extrabold">Report</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div class="p-4 rounded-md shadow bg-white">
              <div class="flex items-start justify-between">
                <h2 class="mb-2 text-xl font-semibold leading-none text-gray-900 truncate">{{ $currency(data?.total_tax) }}</h2>
              </div>
              <p class="leading-none text-gray-600">{{ $t('Tax') }}</p>
            </div>
            <div class="p-4 rounded-md shadow bg-white">
              <div class="flex items-start justify-between">
                <h2 class="mb-2 text-xl font-semibold leading-none text-gray-900 truncate">{{ $currency(data?.total_commission) }}</h2>
              </div>
              <p class="leading-none text-gray-600">{{ $t('Commission') }}</p>
            </div>
            <div class="p-4 rounded-md shadow bg-white">
              <div class="flex items-start justify-between">
                <h2 class="mb-2 text-xl font-semibold leading-none text-gray-900 truncate">
                  {{ $currency(data?.total_agent_commission) }}
                </h2>
              </div>
              <p class="leading-none text-gray-600">{{ $t('Agent Commission') }}</p>
            </div>
            <div class="p-4 rounded-md shadow bg-white">
              <div class="flex items-start justify-between">
                <h2 class="mb-2 text-xl font-semibold leading-none text-gray-900 truncate">{{ $currency(data?.grand_total) }}</h2>
              </div>
              <p class="leading-none text-gray-600">{{ $t('Grant Total') }}</p>
            </div>
          </div>
        </section>
      </div>
      <div v-if="data" class="my-6">
        <loading-button type="button" @click="exportStart()" :loading="exporting" :disabled="exporting">{{
          $t('Export Sales List to Excel')
        }}</loading-button>
      </div>
    </form>
  </admin-layout>
</template>

<script>
import TextInput from '@/Shared/TextInput';
import SelectInput from '@/Shared/SelectInput';
import VSelectInput from '@/Shared/VSelectInput';
import LoadingButton from '@/Shared/LoadingButton';
import AdminLayout from '@/Layouts/AdminLayout.vue';
import TecSectionTitle from '@/Jetstream/SectionTitle.vue';

export default {
  props: ['data', 'old_form', 'customers', 'users'],
  components: { TextInput, LoadingButton, SelectInput, VSelectInput, AdminLayout, TecSectionTitle },

  data() {
    return {
      token: null,
      exporting: false,
      form: this.$inertia.form({
        _method: 'POST',
        end_date: this.old_form ? this.old_form.end_date : null,
        agent_id: this.old_form ? this.old_form.agent_id : null,
        start_date: this.old_form ? this.old_form.start_date : null,
        customer_id: this.old_form ? this.old_form.customer_id : null,
      }),
    };
  },

  mounted() {
    this.token = document.querySelector('meta[name="csrf-token"]').content;
  },

  methods: {
    exportStart() {
      this.exporting = true;
      this.$refs.exportList.submit();
      setInterval(() => (this.exporting = false), 3000);
    },
    reload() {
      this.form.post(route('report.get', this.form), {
        preserveState: false,
        preserveScroll: false,
        // onSuccess: page => {
        //   console.log(page);
        // },
      });
      //   this.$inertia.post(route('report', this.form), { preserveState: false, preserveScroll: false });
    },
  },
};
</script>
